import { CaseStudyBlock, Project } from '../types';

const textBlock = (
  id: string,
  title: string,
  body: string
): CaseStudyBlock => ({
  type: 'text',
  id,
  title,
  body,
});

const wideImageBlock = (
  id: string,
  media: string,
  caption: string,
  size?: 'full' | 'half'
): CaseStudyBlock => ({
  type: 'wide-image',
  id,
  media,
  caption,
  size,
});

const fullBleedVideoBlock = (
  id: string,
  media: string,
  caption: string,
  format?: Project['type']
): CaseStudyBlock => ({
  type: 'full-bleed-video',
  id,
  media,
  caption,
  format,
});

const mobileStoryBlock = (
  id: string,
  media: string[],
  sections: Array<{ id: string; title: string; body: string }>,
  caption?: string
): CaseStudyBlock => ({
  type: 'double-mobile-scroll',
  id,
  media,
  caption,
  sections,
});

export const projects: Project[] = [
  {
    id: '1',
    title: 'Bonhomme Portfolio',
    type: 'desktop',
    context: 'Agency: Bonhomme',
    year: 2024,
    techStack: ['Next.js', 'Sanity', 'GSAP', 'Lenis', 'Zustand'],
    links: [{ label: 'Website', url: 'https://bonhommeparis.com/en' }],
    description:
      'I developed the new portfolio for Bonhomme, with content managed through Sanity for dynamic and flexible updates. The portfolio features carefully crafted animations and transitions, ensuring a seamless and enjoyable user experience.',
    thumbnail: '/thumbnails/bonhomme-portfolio.webp',
    projectMedia: ['/projects/bonhomme/0.webm', '/projects/bonhomme/1.webp'],
    caseStudy: [
      textBlock(
        'bonhomme-intent',
        'The Intent: A Living, Modular Platform',
        'The primary challenge was to build a <strong>fully modular site</strong> that would allow the agency to manage its projects with <strong>complete autonomy</strong>. I designed this architecture to enable dynamic and flexible updates via <strong>Sanity</strong>, ensuring that the layout logic remains robust regardless of the content being added. This system allows the team to compose complex case studies from a library of <strong>10+ interchangeable blocks</strong> without ever needing to touch the code.'
      ),
      fullBleedVideoBlock(
        'bonhomme-motion',
        '/projects/bonhomme/0.webm',
        'Immersive and Seamless Navigation'
      ),
      textBlock(
        'bonhomme-craft',
        'The Craft: Hand-crafted Transitions & Animations',
        'To deliver a polished and high-end feel, I manually tuned <strong>every element’s entrance</strong> and <strong>every transition cycle</strong> using <strong>GSAP</strong> and <strong>Vanilla JS</strong>. By synchronizing these animations with <strong>Zustand</strong> and <strong>Lenis</strong>, I achieved a seamless navigation where projects flow into each other with perfect timing. My focus was on technical performance, maintaining a <strong>consistent 60 FPS</strong> even when dynamically loading <strong>high-resolution media</strong>, to ensure a fluid experience across all devices.'
      ),
      wideImageBlock(
        'bonhomme-cms',
        '/projects/bonhomme/1.webp',
        'Sanity CMS: Editorial Interface'
      ),
      textBlock(
        'bonhomme-structure',
        'The Structure: A CMS Built for Storytelling',
        'Beyond just a content manager, the CMS was configured to act as a <strong>true storytelling tool</strong>. My role was to develop a front-end logic using <strong>Next.js</strong> capable of automatically preserving the agency’s <strong>strict proportions</strong> and <strong>visual hierarchy</strong>. Whether it is an asymmetrical grid or a full-bleed video, the system handles the spacing and alignment rules autonomously, keeping the <strong>art direction intact</strong> at all times.'
      ),
    ],
  },
  {
    id: '2',
    title: "Soutine's Portraits",
    type: 'desktop',
    context: "School Project x Musée de l'Orangerie",
    year: 2023,
    techStack: ['Vue.js', 'GSAP', 'Mediapipe'],
    links: [{ label: 'Website', url: 'https://soutine.netlify.app/' }],
    description:
      "<strong>Soutine's Portraits</strong> is an immersive wall projection experience aimed at making art more accessible in an engaging way. The goal is to immerse users in the world of Soutine's artworks by actively involving them in the restoration of his damaged portraits. To achieve this, users must move in front of the paintings and answer questions related to the artworks. Each correct answer triggers an anecdote about the artist, the history of the portrait, or Paul Guillaume, the museum's gallery owner. Once the restoration is complete, the artwork comes to life through animations.<br /><br />This project was developed in collaboration with the Musée de l'Orangerie. We had two weeks for production: one week for ideation and one week for development, working in a team of 3 developers and 2 designers.",
    thumbnail: '/thumbnails/soutine.webp',
    projectMedia: [
      '/projects/soutine/0.webm',
      '/projects/soutine/1.webm',
      '/projects/soutine/2.webm',
      '/projects/soutine/3.webp',
    ],
    caseStudy: [
      textBlock(
        'soutine-concept',
        'The Intent: Making Art Accessible through Interaction',
        'This <strong>immersive wall projection</strong> was designed to bridge the gap between <strong>classical art</strong> and <strong>digital engagement</strong>. Users are invited to step into the world of painter <strong>Chaïm Soutine</strong> by actively participating in the restoration of his damaged portraits. Through a series of <strong>quizzes</strong> and <strong>physical gestures</strong>, each correct interaction reveals historical anecdotes and eventually <strong>breathes life into the paintings</strong> through animation.'
      ),
      fullBleedVideoBlock(
        'soutine-projection',
        '/projects/soutine/0.webm',
        'The Experience Gateway: Start Screen'
      ),
      textBlock(
        'soutine-craft',
        'The Craft: Engineering Intuitive Hand-Tracking',
        'To create a natural interface, I engineered a <strong>computer-vision pipeline</strong> using <strong>Mediapipe</strong> to track hand gestures in <strong>real-time</strong>. I moved beyond basic landmark detection by implementing a <strong>vector-based recognition system</strong>. To detect a "closed hand" as a click, I analyzed the alignment and direction of vectors between specific landmarks <strong>(9, 11, and 12)</strong>. This refinement was crucial to achieve a <strong>low-latency interaction</strong>, maintaining <strong>sub-100ms response times</strong>, ensuring the museum installation felt immediate and magical.'
      ),
      wideImageBlock(
        'soutine-logic',
        '/projects/soutine/3.webp',
        'Gesture Recognition Logic: Vector Analysis',
        'half'
      ),
      textBlock(
        'soutine-selection',
        'The Selection: Gesture-Based Navigation',
        "The experience allows for <strong>intuitive navigation</strong> between the artist's different works. By utilizing the <strong>hand-recognition system</strong>, users can explore the virtual gallery and select the portrait they wish to restore. This transition phase ensures <strong>fluid continuity</strong> between the landing screen and the start of the educational activity."
      ),
      fullBleedVideoBlock(
        'soutine-navigation',
        '/projects/soutine/1.webm',
        'Art Selection: Gesture-Based Navigation'
      ),
      textBlock(
        'soutine-structure',
        'The Structure: Gamifying Art Restoration',
        'The experience is structured as an <strong>interactive loop</strong>: <strong>selection</strong>, <strong>restoration</strong>, and <strong>discovery</strong>. Once a portrait is selected, the user engages in a quiz to repair specific zones of the canvas. My role involved mapping these interactions to <strong>dynamic visual feedback</strong>, triggering <strong>GSAP animations</strong> that simulate the restoration process and awaken the characters once the work is complete.'
      ),
      fullBleedVideoBlock(
        'soutine-restoration',
        '/projects/soutine/2.webm',
        'The Restoration Quiz: Learning through Play'
      ),
    ],
  },
  {
    id: '3',
    title: "Leïly's Journal",
    type: 'desktop',
    context: 'School Project',
    year: 2024,
    techStack: ['React-Three-Fiber', 'Rapier', 'Framer Motion', 'tiny-emitter'],
    links: [
      { label: 'Website', url: 'https://le-journal-de-leily.netlify.app/' },
      { label: 'Demo Version', url: 'https://demo--le-journal-de-leily.netlify.app/' },
      { label: 'Video Demo', url: 'https://youtu.be/Pn8iTreHANw' },
      { label: 'Video Full', url: 'https://youtu.be/609yH-fqjbc' },
    ],
    description:
      "Through a web-based narrative experience, we tell the story of Leïly, a young Franco-Japanese girl, and her mother. By exploring the 3D scenes of the travel journal they shared, we follow Leïly's journey to Japan, alongside the journey of her mother, who left Japan to settle in France. This immersive work delves into themes of identity, cultural roots, and family bonds.<br /><br />This project is inspired by the true story of Leïly, whom we met during interviews with people of multicultural backgrounds. Her journey to explore her Franco-Japanese roots served as the foundation for our creation.<br /><br />During this 10-week project, we worked as a team of two developers and two designers. As a developer, the main challenges were related to <strong>performance optimization</strong> and managing the <strong>physics</strong> of the characters. I implemented <strong>realistic movement</strong> for character navigation within the 3D scenes while ensuring maximum fluidity. Additionally, the <strong>dynamic folding of 3D objects</strong> in a popup style added another layer of technical complexity, requiring precise synchronization between animation and interaction.",
    thumbnail: '/thumbnails/leily.webp',
    projectMedia: [
      '/projects/leily/0.webm',
      '/projects/leily/1.webm',
      '/projects/leily/2.webm',
      '/projects/leily/3.webm',
    ],
    caseStudy: [
      textBlock(
        'leily-story',
        'The Intent: A Digital Pop-up of Bicultural Identity',
        'Leïly’s Journal is an <strong>immersive 3D web experience</strong> exploring the quest for identity within a <strong>bicultural family</strong>. The narrative follows Leïly as she receives her mother Kazuko’s travel journal from <strong>20 years ago</strong>, prompting a parallel journey back to her roots. The experience is designed as a <strong>virtual pop-up book</strong> where users navigate <strong>dual stories</strong> between Paris and Japan.'
      ),
      fullBleedVideoBlock(
        'leily-world',
        '/projects/leily/0.webm',
        "The Narrative Catalyst: Kazuko's Letter"
      ),
      textBlock(
        'leily-craft',
        'The Craft: Technical Orchestration of a 3D World',
        'To translate the feeling of a <strong>physical book</strong> into the browser, the experience relies on <strong>3D environments that unfold like pop-ups</strong>. I focused on the technical orchestration using <strong>React Three Fiber</strong> and <strong>Framer Motion</strong> to ensure these complex transitions remained fluid and performant. My role was to build a <strong>robust architecture</strong> capable of handling <strong>intricate object transformations</strong> while maintaining a <strong>consistent 60 FPS</strong>, ensuring the technology supports the emotional pace of the story.'
      ),
      fullBleedVideoBlock(
        'leily-popup',
        '/projects/leily/1.webm',
        'Virtual Pop-up Mechanics: Scene Initialization'
      ),
      textBlock(
        'leily-structure',
        'The Structure: Physics and Event-Driven Exploration',
        'I implemented a navigation system using <strong>Rapier</strong> to manage <strong>character physics</strong> and <strong>environmental interactions</strong>. This setup allows users to explore the <strong>3D diorama</strong> freely and interact with symbolic objects that trigger memories or anecdotes. Instead of a centralized state, I used <strong>tiny-emitter</strong> to manage communication between the 3D world and the UI, facilitating a <strong>decoupled</strong> and <strong>flexible event-driven system</strong>.'
      ),
      fullBleedVideoBlock(
        'leily-exploration',
        '/projects/leily/2.webm',
        'Interactive Diorama: Exploration and Discovery'
      ),
      textBlock(
        'leily-duality',
        'Narrative Duality: Book Rotation Mechanics',
        "A key feature of the experience is the ability to <strong>swap perspectives</strong> between the two protagonists by physically rotating the <strong>3D book</strong>. This required precise management of <strong>environmental visibility</strong> and <strong>scene states</strong> to transition between the distinct atmospheres of each character. Using the <strong>event emitter</strong> to trigger these rotations, the technical fluidity was essential to highlight the <strong>shared quest for identity across generations</strong>, from Kazuko's arrival in France to Leïly's return to Japan."
      ),
      fullBleedVideoBlock(
        'leily-bridge',
        '/projects/leily/3.webm',
        'The Bridge: Switching Character Perspectives'
      ),
    ],
  },
  {
    id: '4',
    title: 'Hestia',
    type: 'desktop',
    context: 'School Project',
    year: 2024,
    techStack: ['React-Three-Fiber', 'Redux', 'Lottie', 'Drei'],
    links: [
      { label: 'Website', url: 'https://hestia-experience.netlify.app/' },
    ],
    description:
      "<strong>Hestia</strong> is an interactive experience created in two weeks as part of a workshop on WebGL and binaural sound at Gobelins. Inspired by the theme of the postcard, the project immerses the user in the past by reconstructing the story of a received letter through an immersive scene.<br /><br />The first week focused on ideation (concept, art direction, storytelling, sound design), while the second week was dedicated to production and development. As a developer, I primarily worked on the <strong>dynamic camera</strong>, creating a <strong>path</strong> synchronized with the user's <strong>scroll</strong>, a <strong>camera target</strong> to orient the view around key objects, and a <strong>mouse-reactive movement</strong> to enhance immersion. It was my first time using <strong>React Three Fiber</strong>, which was an enriching experience for handling 3D on the web.<br /><br />The user uncovers the story by collecting objects that rekindle Hestia's hearth, illuminating and animating the scene to piece together the past.",
    thumbnail: '/thumbnails/hestia.webp',
    projectMedia: [
      '/projects/hestia/0.webm',
      '/projects/hestia/1.webm',
      '/projects/hestia/2.webm',
    ],
    caseStudy: [
      textBlock(
        'hestia-premise',
        'The Intent: A 70 Year Old Postcard',
        'After discovering a postcard dating back <strong>70 years</strong>, the user is plunged into the heart of Hestia’s home. This interactive experience reconstructs the story behind a forgotten letter, allowing the user to explore the intimate daily life of its characters. By collecting lost objects, users gradually <strong>reignite the hearth</strong>, restoring both light and memory to the scene.'
      ),
      fullBleedVideoBlock(
        'hestia-camera',
        '/projects/hestia/0.webm',
        'Camera Path and Object Interaction'
      ),
      textBlock(
        'hestia-craft',
        'The Craft: Rapid Learning and Spatial Audio',
        "During a rapid <strong>4 day development sprint</strong>, which served as my <strong>first experience with React Three Fiber</strong>, I developed a <strong>scroll synchronized camera system</strong>. The camera follows a predefined path while remaining reactive to mouse movements, heightening the sense of presence within the diorama. To deepen immersion, I integrated <strong>binaural audio</strong> where the volume and spatial positioning of sounds are tied to the user's proximity to specific objects. This <strong>spatialized audio feedback</strong> acts as a compass: as the camera approaches an interactive element, the audio cues become clearer, guiding the player through the darkened environment."
      ),
      fullBleedVideoBlock(
        'hestia-audio',
        '/projects/hestia/1.webm',
        'Proximity Based Audio Cues'
      ),
      textBlock(
        'hestia-structure',
        'The Structure: Lighting Transitions and Resource Efficiency',
        'To achieve <strong>high quality visuals</strong> while maintaining <strong>resource efficient rendering</strong>, the experience uses a system of <strong>baked textures</strong>. To switch between states, a <strong>custom shader</strong> mixes a "Dark" map and a "Light" map in real time. Upon interaction with an object, the system coordinates a local transition between these two states, allowing the environment to brighten dynamically. This approach simulates a <strong>hearth being rekindled</strong> with perceptual smoothness, providing rich visual feedback without the performance cost of <strong>real time lights</strong>.'
      ),
      fullBleedVideoBlock(
        'hestia-lighting',
        '/projects/hestia/2.webm',
        'Interactive Restoration: Lighting Transition'
      ),
    ],
  },
  {
    id: '5',
    title: 'Tag Heuer - Xmas',
    type: 'mobile',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    techStack: ['VanillaJS', 'Lottie', 'GSAP'],
    links: [
      {
        label: 'Not available anymore',
        url: 'N/A',
      },
    ],
    description:
      'This project for Tag Heuer involved developing a mobile web game for Christmas, where the goal was to assemble a watch in the correct order as quickly as possible. I was responsible for the development of both the pre-game and post-game sections, particularly focusing on animations and transitions to ensure a smooth and engaging user experience.',
    thumbnail: '/thumbnails/tagheuer-xmas.webp',
    projectMedia: ['/projects/th-xmas/0.webm', '/projects/th-xmas/1.webm'],
    caseStudy: [
      mobileStoryBlock('th-xmas-story', ['/projects/th-xmas/0.webm'], [
        {
          id: 'th-xmas-intent',
          title: 'The Intent: A Precision Assembly Challenge',
          body: 'For this Christmas campaign, I helped develop a mobile web game designed to showcase <strong>TAG Heuer&apos;s watchmaking heritage</strong>. Users are challenged to assemble a <strong>luxury watch</strong> by placing its components in the correct order as quickly as possible. The goal was to create a <strong>fast-paced, high-end digital experience</strong> that reflects the precision and elegance of the brand.'
        },
        {
          id: 'th-xmas-craft',
          title: 'The Craft: Dynamic Loading and Motion Design',
          body: 'I was responsible for the <strong>pre-game and post-game sections</strong> of the experience, with a heavy focus on <strong>motion design</strong> and <strong>user engagement</strong>. Using <strong>GSAP</strong>, I engineered a sophisticated loading sequence that transitions the user into the holiday-themed environment. By carefully managing the <strong>timing</strong> and <strong>easing</strong> of these animations, I ensured that the technical transition remained fluid and visually aligned with the premium brand identity.'
        },
      ], 'High-Performance Loading: GSAP Sequencing'),
      textBlock(
        'th-xmas-structure',
        'The Structure: Seamless Transitions via CSS Clip-Path',
        'To maintain a high level of <strong>perceptual smoothness</strong>, I implemented custom transitions between different game states. I utilized <strong>CSS clip-path animations</strong> to create an elegant, non-intrusive reveal as the game initializes. This approach allowed for complex visual changes without the performance overhead of <strong>heavy JavaScript-driven geometry</strong>, ensuring a responsive feel on mobile devices while keeping the focus on the intricate watch components.'
      ),
      fullBleedVideoBlock(
        'th-xmas-reveal',
        '/projects/th-xmas/1.webm',
        'State Transitions: CSS Clip-Path Reveal'
      ),
    ],
  },
  {
    id: '6',
    title: 'Tag Heuer - Golf',
    type: 'desktop',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    techStack: ['VanillaJS', 'anime.js'],
    links: [
      {
        label: 'Not available anymore',
        url: 'N/A',
      },
    ],
    description:
      'I developed several pages for the Tag Heuer website, including this one dedicated to the Calibre E4 Golf Edition watch. The page features numerous scroll-based animations to create an immersive and dynamic user experience.',
    thumbnail: '/thumbnails/tagheuer-golf.webp',
    projectMedia: ['/projects/th-golf/0.webm', '/projects/th-golf/1.webm'],
    caseStudy: [
      textBlock(
        'th-golf-intent',
        'The Intent: A Dynamic Product Experience',
        'For the launch of the <strong>TAG Heuer Connected Calibre E4 Golf Edition</strong>, I developed several dedicated pages designed to highlight the watch&apos;s <strong>technical performance</strong> and <strong>golf-specific features</strong>. The goal was to create an <strong>immersive digital journey</strong> where the product feels alive and responsive as the user explores its capabilities.'
      ),
      fullBleedVideoBlock(
        'th-golf-landing',
        '/projects/th-golf/0.webm',
        'Immersive Landing: Scroll to Discover'
      ),
      textBlock(
        'th-golf-craft',
        'The Craft: Scroll-Based Narrative',
        'I focused on creating a <strong>seamless flow</strong> using numerous <strong>scroll-based animations</strong> to guide the user through the watch&apos;s features. Using <strong>Anime.js</strong> and <strong>Vanilla JS</strong>, I engineered transitions that trigger specifically during the user&apos;s journey, from the initial <strong>"Scroll to Discover"</strong> entry to detailed feature reveals. This approach ensures that the narrative pace is directly controlled by the user, making the exploration of the Calibre E4 both interactive and intuitive.'
      ),
      textBlock(
        'th-golf-structure',
        'The Structure: High-End UI and Fluidity',
        'The project required a high level of <strong>technical precision</strong> to ensure that complex animations remained fluid across different devices. By utilizing an <strong>efficient animation stack</strong>, I maintained a high level of <strong>perceptual smoothness</strong> as the user navigates through lifestyle imagery and product close-ups. Every transition was designed to reflect the precision of the TAG Heuer brand, creating a digital experience as refined as the physical watch.'
      ),
      fullBleedVideoBlock(
        'th-golf-features',
        '/projects/th-golf/1.webm',
        'Feature Exploration: Scroll-Based Interaction'
      ),
    ],
  },
  {
    id: '7',
    title: 'Tag Heuer - Takeovers',
    type: 'desktop',
    context: 'Agency: Bonhomme, Client: Tag Heuer',
    year: 2024,
    links: [
      {
        label: 'Website',
        url: 'https://www.tagheuer.com',
      },
    ],
    techStack: ['VanillaJS', 'GSAP'],
    description:
      "I developed multiple animations and pop-ups (takeovers) for the Tag Heuer website to showcase the brand's latest releases. My role involved creating engaging animations and optimizing transitions to provide a smooth and dynamic user experience.",
    thumbnail: '/thumbnails/tagheuer-takeovers.webp',
    projectMedia: [
      '/projects/th-takeovers/0.webm',
      '/projects/th-takeovers/1.webm',
      '/projects/th-takeovers/2.webm',
      '/projects/th-takeovers/3.webp',
    ],
    caseStudy: [
      textBlock(
        'th-takeovers-intent',
        'The Intent: Enhancing Brand Visibility',
        'I developed multiple <strong>takeover components</strong> and <strong>interactive popups</strong> for the TAG Heuer website to spotlight major product launches and global <strong>Formula 1 partnerships</strong>. The objective was to create <strong>eye-catching marketing modules</strong> that integrate seamlessly into the existing site architecture while driving user engagement through <strong>high-quality motion design</strong>.'
      ),
      fullBleedVideoBlock(
        'th-takeovers-logo',
        '/projects/th-takeovers/0.webm',
        'Branding Motion: Dynamic Header Logo Animation'
      ),
      textBlock(
        'th-takeovers-craft',
        'The Craft: Sprite Sheet Optimization and Engaging UI',
        'For the primary branding animations and <strong>call-to-action modules</strong>, I implemented a <strong>Sprite Sheet animation method</strong>. This technique allowed for the rendering of <strong>complex, high-fidelity character and logo movements</strong> while significantly reducing <strong>CPU overhead</strong>. By managing these sequences through code, I ensured that both the header animations and the interactive popups remained <strong>crisp and responsive</strong>, providing a premium feel that aligns with the brand’s precision-oriented identity.'
      ),
      fullBleedVideoBlock(
        'th-takeovers-cta',
        '/projects/th-takeovers/1.webm',
        'Conversion Focus: Interactive Call-to-Action Reveal'
      ),
      textBlock(
        'th-takeovers-structure',
        'The Structure: Real-Time Data and Racing Synchronization',
        'To support TAG Heuer&apos;s role in <strong>racing</strong>, I developed <strong>live event modules</strong>, including a high-visibility <strong>Formula 1 race countdown timer</strong>. Additionally, I implemented a custom <strong>"World Time" footer component</strong> featuring an interactive watch face. This module uses <strong>real-time synchronization</strong> to display the current time based on the user&apos;s local timezone, with live-updating movement for the watch hands. This blend of <strong>real-time data</strong> and <strong>aesthetic precision</strong> provides a functional yet premium touch to the site’s global navigation.'
      ),
      fullBleedVideoBlock(
        'th-takeovers-race',
        '/projects/th-takeovers/2.webm',
        'Live Event Tracking: Formula 1 Race Countdown'
      ),
      wideImageBlock(
        'th-takeovers-world-time',
        '/projects/th-takeovers/3.webp',
        'Global Utility: Real-Time Timezone Footer'
      ),
    ],
  },
  {
    id: '8',
    title: 'Aux Petits Soins',
    type: 'mobile',
    context: 'School Project',
    year: 2025,
    links: [
      {
        label: 'Full Experience Walkthrough',
        url: 'https://youtu.be/S3pE2wAVifg',
      },
    ],
    techStack: ['Rive', 'Expo', 'React Native'],
    description:
      'Aux Petits Soins is an interactive mobile guide designed to teach users how to maintain household appliances through gamification. I developed two of the four core mini games, focusing on creating a playful yet precise learning environment.',
    thumbnail: '/thumbnails/petits-soins.webp',
    projectMedia: [
      '/projects/petits-soins/2.webm',
      '/projects/petits-soins/0.webm',
      '/projects/petits-soins/1.webm',
      '/projects/petits-soins/3.webp',
    ],
    caseStudy: [
      mobileStoryBlock(
        'petits-soins-puzzle-story',
        ['/projects/petits-soins/0.webm'],
        [
          {
            id: 'petits-soins-intent',
            title: 'The Intent: A Digital Care Guide',
            body: 'Aux Petits Soins is an interactive mobile guide designed to teach users how to maintain household appliances through gamification. I developed <strong>two of the four core mini games</strong>, focusing on creating a <strong>playful yet precise learning environment</strong>. The experience transforms routine maintenance, such as <strong>descaling a coffee machine</strong>, into an engaging series of challenges that combine <strong>logic</strong> and <strong>dexterity</strong>.',
          },
          {
            id: 'petits-soins-craft',
            title: 'The Craft: Rapid Prototyping on the Edge',
            body: 'Having no prior experience with <strong>Rive</strong> at the start of the project, I immediately underwent a comprehensive training phase to explore the tool&apos;s full potential. Because Rive is an evolving platform still in active development, I focused on <strong>rapid prototyping</strong> to identify <strong>technical boundaries</strong> and <strong>performance limits</strong> early on. This proactive approach allowed me to engineer a <strong>click and drop puzzle system</strong> for the first mini game and a <strong>responsive cleaning simulation</strong> for the second, ensuring a stable experience despite the experimental nature of the animation engine.<br /><br />To enhance the physicality of the digital interaction, I synchronized <strong>Expo Haptics</strong> with the <strong>Rive state machine</strong>. This created a tactile scrubbing sensation: as the user drags a cloth over a stain, the haptic intensity scales with the remaining opacity of the stain, providing <strong>real time physical feedback</strong> that mimics the effort of cleaning.',
          },
        ],
        'Puzzle Game 1: Click and Drop'
      ),
      fullBleedVideoBlock(
        'petits-soins-rive-prototyping',
        '/projects/petits-soins/2.webm',
        'Rive Editor Prototyping for Game 1',
        'desktop'
      ),
      mobileStoryBlock(
        'petits-soins-cleaning-story',
        ['/projects/petits-soins/1.webm'],
        [
          {
            id: 'petits-soins-structure',
            title: 'The Structure: State Driven Animation Logic',
            body: 'The project relied on a complex <strong>state machine</strong> built within the Rive Editor to manage the various interactive transitions of the coffee machine. I bridged this logic to the React Native app by using listeners that monitor Rive events, such as <strong>StainEnter</strong> or <strong>StartDrag</strong>, to drive both the visual opacity changes and the parallel haptic intervals. This architecture allowed for a highly responsive UI where the animation, sound, and touch are perfectly aligned, creating a cohesive sensory loop.',
          },
        ],
        'Cleaning Game 2: Tactile Simulation'
      ),
      wideImageBlock(
        'petits-soins-technical-logic',
        '/projects/petits-soins/3.webp',
        'Technical Logic for Game 2: Haptic & Rive Sync',
        'half'
      ),
    ],
  },
  {
    id: '9',
    title: 'Moooi',
    type: 'desktop',
    context: 'Agency: Build in Amsterdam, Client: Moooi',
    year: 2025,
    techStack: ['Next.js', 'Framer Motion', 'Storyblok', 'React'],
    links: [{ label: 'Website', url: 'https://www.moooi.com/eu/collection' }],
    description:
      'During a five-month immersion at Build in Amsterdam, I joined the team dedicated to Moooi, contributing to the 2.0 refactor of their e-commerce platform and the development of product listing pages for a premium digital retail experience.',
    thumbnail: '/thumbnails/moooi.webp',
    projectMedia: [
      '/projects/moooi/0.webm',
      '/projects/moooi/1.webp',
    ],
    caseStudy: [
      textBlock(
        'moooi-intent',
        'The Intent: A Global Commerce Overhaul',
        'During a <strong>five month immersion</strong> at Build in Amsterdam, I was a core contributor to the <strong>PLP 2.0 Redesign</strong>, a massive project involving <strong>over 100+ merged Pull Requests</strong>. My objective was to transform the <strong>Product Listing Page</strong> into a modern, intuitive, and high performance interface that mirrors Moooi&apos;s luxury positioning. By merging <strong>more than 100 PRs</strong> over the course of the internship, I demonstrated a <strong>high technical velocity</strong>, consistently delivering <strong>production grade code</strong> validated by <strong>daily senior developer reviews</strong>.'
      ),
      fullBleedVideoBlock(
        'moooi-filtering',
        '/projects/moooi/0.webm',
        'Advanced Filtering & Navigation'
      ),
      textBlock(
        'moooi-craft',
        'II. The Craft: Systems Thinking and Interaction Logic',
        'Beyond UI components, I spearheaded strategic technical initiatives such as the engineering of a <strong>multi brand theme switcher</strong> in <strong>Storybook</strong>. This system supports both <strong>Moooi</strong> and <strong>Pastoe</strong>, enabling automatic synchronization of typography, colors, and styles to improve productivity across design and development teams. I also developed the core logic for a new <strong>hierarchical filtering engine</strong>, supporting <strong>multiple cross selections</strong> with <strong>real time product counts</strong> and <strong>smart scroll restoration</strong> to ensure a frictionless transition between listing and product pages.'
      ),
      textBlock(
        'moooi-structure',
        'III. The Structure: Measurable Performance and Accessibility',
        'I led a <strong>rigorous optimization phase</strong> based on <strong>Lighthouse audits</strong> to ensure the site met the highest technical standards. By deferring third party scripts and prioritizing critical image loading, I helped raise the global performance score from <strong>65 to 85</strong>. I also addressed complex <strong>ARIA issues</strong> and <strong>focus management</strong> to boost the accessibility score to <strong>96</strong>, while achieving a perfect <strong>100 in Best Practices</strong>. This work resulted in a <strong>robust, professional grade platform</strong> that successfully balances extravagant design with absolute technical excellence.'
      ),
      wideImageBlock(
        'moooi-scores',
        '/projects/moooi/1.webp',
        'Lighthouse Audit Results',
        'half'
      ),
    ],
  },
];
